import { logoutUrl } from '@procurify/authentication/src/configs'
import * as HttpStatus from 'http-status-codes'

/**
 * Backend error codes on a 401 that mean the session is gone and the user
 * must sign in again.
 *
 * `TRUE_USER_UNAVAILABLE` comes from `IsNotImpersonatedPermission` when
 * `request.true_user` is missing — mostly Safari/WebKit expiring the
 * `access_token` cookie after 7 days while `sessionid` stays
 * (`PLAT-5313`). `STAFF_ACCESS_EXPIRED` is raised when a Procurify
 * staff member's time-boxed access to a customer domain has lapsed.
 */
export const SessionExpiredErrorCodes = {
  TRUE_USER_UNAVAILABLE: 'TRUE_USER_UNAVAILABLE',
  STAFF_ACCESS_EXPIRED: 'STAFF_ACCESS_EXPIRED',
} as const

/**
 * DRF's `not_authenticated` message, which the backend deliberately reuses
 * for `TRUE_USER_UNAVAILABLE` so older clients keyed on the text still
 * recover (procurifydevelopment#13957).
 */
export const InvalidSessionMessage = {
  MISSING: 'Authentication credentials were not provided',
} as const

interface SessionErrorBody {
  errors?: Record<string, { code?: unknown; message?: unknown } | undefined>
}

const asSessionErrorBody = (body: unknown): SessionErrorBody | null =>
  typeof body === 'object' && body !== null ? (body as SessionErrorBody) : null

export const hasMissingCredentials = (body: unknown): boolean => {
  const detail = asSessionErrorBody(body)?.errors?.detail

  return (
    detail?.code === SessionExpiredErrorCodes.TRUE_USER_UNAVAILABLE ||
    (typeof detail?.message === 'string' &&
      detail.message.includes(InvalidSessionMessage.MISSING))
  )
}

export const hasStaffAccessExpired = (body: unknown): boolean =>
  asSessionErrorBody(body)?.errors?.[
    SessionExpiredErrorCodes.STAFF_ACCESS_EXPIRED
  ]?.code === SessionExpiredErrorCodes.STAFF_ACCESS_EXPIRED

export const isSessionExpiredResponse = (
  status: number | undefined,
  body: unknown
): boolean =>
  status === HttpStatus.UNAUTHORIZED &&
  (hasMissingCredentials(body) || hasStaffAccessExpired(body))

/**
 * Sends the user to the backend logout route, which clears cookies and
 * bounces through Auth0 back to sign-in. Deliberately not single-flight:
 * repeated `replace` calls to the same URL are harmless, whereas a sticky
 * flag would block every later redirect if the user cancelled a
 * `beforeunload` prompt.
 */
export const redirectToSignIn = (): void => {
  window.location.replace(logoutUrl)
}
